import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './ManageTeamModalContent.module.scss';
import { useTranslation } from 'react-i18next';
import CloseButton from '../CloseButton/CloseButton';
import { useUserContext } from '../../context/UserContext';
import Icon from '../Icon/Icon';
import { CustomButton } from '../Button/Button';
import MiniUserDetail from '../MiniUserDetail/MiniUserDetail';
import ManageTeamModalUser from '../ManageTeamModalUser/ManageTeamModalUser';

const ManageTeamModalContent = ({ users, permissions, videoAuthor, onClose, onSubmit, onInvite, scenes }) => {
  // Editing user id
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [initialDataByUsers, setInitialDataByUsers] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { user } = useUserContext();

  const [dataByUsers, setDataByUsers] = useState(null);

  const compareArrays = (initial, changed) => {
    const initialDataSorted = initial.sort((a, b) => a - b);
    const changedDataSorted = changed.sort((a, b) => a - b);
    for (let i = 0; i < initialDataSorted.length; i++) {
      if (initialDataSorted[i] !== changedDataSorted[i]) {
        return false;
      }
    }
    return true;
  };

  const isOwner = user.username === videoAuthor.username;

  const changedData = useMemo(() => {
    const dataToUpdate = {};

    // Compare initial data with changed to send only data that needs changes for onSubmit function
    for (const userId in initialDataByUsers) {
      if (initialDataByUsers[userId].scenes.length === dataByUsers[userId].scenes.length) {
        const hasChanges = compareArrays(initialDataByUsers[userId].scenes, dataByUsers[userId].scenes);
        if (!hasChanges) {
          dataToUpdate[userId] = {
            scenes: structuredClone(dataByUsers[userId].scenes),
          };
        }
      } else {
        dataToUpdate[userId] = {
          scenes: structuredClone(dataByUsers[userId].scenes),
        };
      }
      const newReadPermission = initialDataByUsers[userId].permissions.read !== dataByUsers[userId].permissions.read;
      const newUpdatePermission = initialDataByUsers[userId].permissions.update !== dataByUsers[userId].permissions.update;
      if (newReadPermission || newUpdatePermission) {
        if (dataToUpdate[userId]) {
          dataToUpdate[userId].permissions = {};
        } else {
          dataToUpdate[userId] = { permissions: {} };
        }
      }

      if (newReadPermission) {
        dataToUpdate[userId].permissions.read = dataByUsers[userId].permissions.read;
      }
      if (newUpdatePermission) {
        dataToUpdate[userId].permissions.update = dataByUsers[userId].permissions.update;
      }
    }

    return dataToUpdate;
  }, [dataByUsers, initialDataByUsers]);

  const selectUser = userId => {
    setSelectedUserId(prevState => (prevState === userId ? null : userId));
  };

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      await onSubmit(changedData);
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
    onClose();
  }, [changedData]);

  const handleInvite = () => {
    if (Object.keys(changedData)?.length) {
      const isConfirm = confirm(t('storyboard.editor.manage_team.confirm_go_to_invite'));

      if (!isConfirm) {
        return;
      }
    }

    onInvite();
  };

  useEffect(() => {
    if (selectedUserId === null && dataByUsers === null) {
      const sortedByUsers = {};
      scenes
        .filter(scene => scene.author && scene?.author.username !== videoAuthor.username)
        .forEach(scene => {
          if (sortedByUsers[scene.author.username]) {
            sortedByUsers[scene.author.username].scenes.push(scene.id);
          } else {
            sortedByUsers[scene.author.username] = { scenes: [scene.id] };
          }
        });

      permissions.forEach(item => {
        if (item.username && item?.username !== videoAuthor.username) {
          if (!sortedByUsers[item.username]) {
            sortedByUsers[item.username] = { scenes: [] };
          }
        }
      });

      permissions.forEach(item => {
        if (sortedByUsers[item.username]) {
          const userPermissions = {
            read: item.storyPermissions?.find(setting => setting.action === 'read') !== undefined,
            update: item.storyPermissions?.find(setting => setting.action === 'update') !== undefined,
          };
          sortedByUsers[item.username].permissions = userPermissions;
        }
      });

      setDataByUsers(sortedByUsers);
      setInitialDataByUsers(sortedByUsers);
    }
  }, [scenes, permissions]);

  const getSceneAssignee = useCallback((sceneId) => {
    if (!sceneId) {
      return null;
    }
    let sceneAuthorUsername = null;
    for (let key in dataByUsers) {
      if (dataByUsers[key].scenes.find(item => item === sceneId) && key !== selectedUserId) {
        sceneAuthorUsername = key;
      }
    }
    if (sceneAuthorUsername) {
      return { name: users.find(user => user.username === sceneAuthorUsername).name, userId: sceneAuthorUsername }
    }
    return null;
  }, [dataByUsers, scenes, selectedUserId, users]);

  const updateUserData = (userId, sceneId = null, settings = null) => {
    const alreadyBelongsToThisUser = dataByUsers[userId].scenes.find(item => item === sceneId);
    const assignedTo = getSceneAssignee(sceneId);
    if (assignedTo) {
      const confirmSwitch = confirm(
        t('storyboard.invite_modal.change_clip_owner_confirm', { name: assignedTo.name })
      );
      if (!confirmSwitch) {
        return;
      }
      setDataByUsers(prevState => ({
        ...prevState,
        [userId]: { ...prevState[userId], ...(sceneId ? { scenes: [...prevState[userId].scenes, sceneId] } : {}), ...(settings ? { permissions: { ...prevState[userId].permissions, ...settings } } : {}) },
        ...(assignedTo ? {
          [assignedTo?.userId]: {
            ...prevState[assignedTo.userId],
            scenes: prevState[assignedTo.userId].scenes.filter(item => item !== sceneId),
          }
        } : {}),
      }));
      return;
    }
    if (alreadyBelongsToThisUser) {
      setDataByUsers(prevState => ({
        ...prevState,
        [userId]: { ...prevState[userId], ...(sceneId ? { scenes: prevState[userId].scenes.filter(item => item !== sceneId) } : {}), ...(settings ? { permissions: { ...prevState[userId].permissions, ...settings } } : {}) },
      }));
      return;
    }
    setDataByUsers(prevState => ({
      ...prevState,
      [userId]: { ...prevState[userId], ...(sceneId ? { scenes: [...prevState[userId].scenes, sceneId] } : {}), ...(settings ? { permissions: { ...prevState[userId].permissions, ...settings } } : {}) },
    }));
  }

  const deleteUser = useCallback(
    userId => {
      setDataByUsers(prevState => ({
        ...prevState,
        [userId]: { scenes: [], permissions: { read: false, update: false } },
      }))
      setSelectedUserId(null)
    }, []);

  return (
    <>
      <div className={styles.headerContainer}>
        <h1 className={styles.header}>{t('storyboard.editor.manage_team.header')}</h1>
        <CloseButton onClose={onClose} />
      </div>
      <div className={styles.usersList}>
        {<div className={styles.ownerContainer}>
          <MiniUserDetail avatar={isOwner ? user.avatarUri : videoAuthor.avatar} name={isOwner ? user.userFullName : videoAuthor.name} size="small" />
          <span className={styles.label}>{`(${t(isOwner ? 'share_folder_modal.first_step.you' : 'storyboard.clip_organizer.owner').toLowerCase()})`}</span>
        </div>}
        {dataByUsers &&
          users
            .map(
              (user, index) =>
                <ManageTeamModalUser
                  key={index}
                  user={user}
                  index={index}
                  scenes={scenes}
                  selected={user.username == selectedUserId}
                  data={dataByUsers[user.username]}
                  selectUser={selectUser}
                  updateData={updateUserData}
                  deleteUser={deleteUser}
                />
            )}
      </div>
      <div className={styles.footer}>
        <CustomButton variant="link" onClick={handleInvite} disabled={isLoading}>
          <Icon src="invite" className={styles.inviteIcon} />
          <span className={styles.inviteText}>{t('storyboard.editor.invite')}</span>
        </CustomButton>
        <CustomButton
          label={t('general.save')}
          variant="primary"
          onClick={handleSubmit}
          disabled={!Object.keys(changedData)?.length}
          className={styles.saveBtn}
          loadingSpinner={true}
          loading={isLoading}
        />
      </div>
    </>
  );
};

export default ManageTeamModalContent;
